import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { ActivityIndicator, Alert, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';

import type { AssociationSettingsHandle } from './association-settings-handle';
import { ImportBudgetModal } from './import-budget-modal';

import { LocalTypeahead } from '@/components/common/local-typeahead';
import {
	useAccountingPermissions, useAssociationDues, useCreateDuesLine,
	useDeleteDuesLine, useGlCodes, useUpdateAssociationDues, useUpdateDuesLine,
} from '@/hooks/use-accounting';
import { usePristinePayload } from '@/hooks/use-pristine-payload';
import { AssessmentCadence, type AssociationDuesLine, type GlCode } from '@/types/models';
import { type DuesRounding, effectiveRounding, periodsPerYear } from '@/utils/accounting-dues';
import { formatMoneyCents } from '@/utils/format-money';

interface Props {
	/** Edit mode: the association whose dues are loaded. Omit for the create
	 *  (draft) flow — only the "Default dues" scalar fields are shown (GL split
	 *  lines + levels need an existing id and are configured after creation),
	 *  and `save(id)` persists them only when the user entered something. */
	associationId?: string;
	/** When true, the parent owns saving via the imperative handle. */
	hideSaveButton?: boolean;
}

const parseDollarsToCents = (s: string): number | null => {
	const n = Number(s.replace(/[^0-9.-]/g, ''));
	if (!isFinite(n)) return null;
	return Math.round(n * 100);
};

/** Blank "Default dues" state for the create (draft) flow. */
const DRAFT_DUES = { amountCents: undefined, cadence: undefined, dayOfMonth: undefined, parValue: false, operatingBudgetCents: undefined, roundToDollar: false, rounding: 'none' } as const;

/**
 * Default dues + tiered levels for an association. Used by the
 * association edit screen, the create flow (draft mode, no id yet), and the
 * per-account DuesModal — all share the same backing data via the same UI.
 *
 * Read access requires VIEW_ACCOUNTS; write controls are hidden when
 * canModify is false.
 */
export const AssociationDuesSettings = forwardRef<AssociationSettingsHandle, Props>(({ associationId, hideSaveButton }, ref) => {
	const { canModify } = useAccountingPermissions();
	const { data: dues, isLoading } = useAssociationDues(associationId);
	const draft = !associationId;

	if (!draft && (isLoading || !dues)) {
		return <View style={{ padding: 16, alignItems: 'center' }}><ActivityIndicator /></View>;
	}

	return (
		<View>
			<AssociationSection ref={ref} associationId={associationId} dues={dues ?? DRAFT_DUES} canModify={canModify} hideSaveButton={hideSaveButton} draft={draft} />
			{associationId && dues && (
				<LinesSection
					associationId={associationId}
					lines={dues.lines}
					duesAmountCents={dues.amountCents}
					parValue={dues.parValue}
					cadence={dues.cadence}
					canModify={canModify}
				/>
			)}
			{draft && (
				<Text style={styles.draftHint}>Save the association to configure GL split lines.</Text>
			)}
		</View>
	);
});

AssociationDuesSettings.displayName = 'AssociationDuesSettings';

/**
 * Par-value rounding. Whole-dollar modes round EACH GL line on its own and bill the sum —
 * how the offices bill (Hunter's Crossing at 1.366%: $262 operating + $52 reserves = $314).
 */
const ROUNDING_OPTIONS: Array<{ value: DuesRounding; label: string; hint: string }> = [
	{ value: 'none', label: 'To the cent', hint: 'Each account is billed its exact share, to the cent.' },
	{
		value: 'nearest', label: 'Nearest dollar',
		hint: 'Each GL line is rounded to the nearest whole dollar and the bill is their sum, so the roster can land a few dollars over or under the budget.',
	},
	{
		value: 'up', label: 'Up to dollar',
		hint: 'Each GL line is rounded UP to the next whole dollar, so the roster never collects less than the adopted budget.',
	},
];

const CADENCES: AssessmentCadence[] = [AssessmentCadence.Monthly, AssessmentCadence.Quarterly, AssessmentCadence.Annual];

const AssociationSection = forwardRef<AssociationSettingsHandle, {
	associationId?: string;
	dues: { amountCents?: number; cadence?: AssessmentCadence; dayOfMonth?: number; parValue: boolean; operatingBudgetCents?: number; roundToDollar?: boolean; rounding?: DuesRounding };
	canModify: boolean;
	hideSaveButton?: boolean;
	draft?: boolean;
}>(({ associationId, dues, canModify, hideSaveButton, draft }, ref) => {
	const [amount, setAmount] = useState(dues.amountCents != null ? (dues.amountCents / 100).toFixed(2) : '');
	const [cadence, setCadence] = useState<AssessmentCadence | undefined>(dues.cadence);
	const [dayOfMonth, setDayOfMonth] = useState(dues.dayOfMonth != null ? String(dues.dayOfMonth) : '');
	const [parValue, setParValue] = useState<boolean>(!!dues.parValue);
	const [rounding, setRounding] = useState<DuesRounding>(effectiveRounding(dues));
	const update = useUpdateAssociationDues();

	// Throws a user-facing message on invalid input. Callers own how to surface
	// it: the inline Save button Alerts; the parent FAB collects it into a
	// single partial-save toast (so the user never gets a double notification).
	const buildBody = useCallback((): {
		amountCents?: number | null; cadence: AssessmentCadence | null; dayOfMonth: number | null;
		parValue: boolean; operatingBudgetCents?: number | null; rounding: DuesRounding;
	} => {
		const cents = amount.trim() === '' ? null : parseDollarsToCents(amount);
		const day = dayOfMonth.trim() === '' ? null : Number(dayOfMonth);
		if (amount.trim() !== '' && cents == null) throw new Error('Invalid amount');
		if (dayOfMonth.trim() !== '' && (!isFinite(day!) || day! < 1 || day! > 31)) throw new Error('Day of month must be 1–31');
		// operating_budget_cents is never written any more — the annual budget is
		// the sum of the GL lines. The column stays as a read-only fallback for
		// associations configured before that change.
		// `roundToDollar` too: a release deploys web before API, and the previous API only knows
		// that toggle. The current API lets `rounding` outrank it.
		const base = { cadence: cadence ?? null, dayOfMonth: day, parValue, rounding, roundToDollar: rounding !== 'none' };
		// Par-value mode computes every amount from the annual GL budget, so it
		// doesn't own the flat amount either. OMITTED, not nulled — JSON.stringify
		// drops undefined and the PUT skips absent fields, so the stored value
		// survives and turning par value back off restores the previous flat dues.
		return parValue ? base : { ...base, amountCents: cents };
	}, [amount, cadence, dayOfMonth, parValue, rounding]);

	// On the create (draft) flow, skip the write entirely when the user left
	// the default-dues fields blank — a new association doesn't need an empty
	// dues row.
	const hasDraftValue = amount.trim() !== '' || cadence != null || dayOfMonth.trim() !== '' || parValue;

	const { isDirty, markSaved } = usePristinePayload(buildBody);

	const persist = useCallback(async (id: string) => {
		await update.mutateAsync({ associationId: id, body: buildBody() });
		markSaved();
	}, [buildBody, update, markSaved]);

	useImperativeHandle(ref, () => ({
		save: async (id: string) => {
			if (draft && !hasDraftValue) return;
			// An untouched section must not write. The edit page flushes EVERY mounted
			// section, so without this a rename fired a dues PUT too — which 403s for a
			// user who may see dues but not save them.
			if (!isDirty) return;
			await persist(id);
		},
	}), [draft, hasDraftValue, isDirty, persist]);

	const save = () => {
		if (!associationId) return;
		void persist(associationId).catch((e) => Alert.alert('Save failed', e instanceof Error ? e.message : 'Unknown error'));
	};

	return (
		<View style={styles.section}>
			<Text style={styles.sectionTitle}>Default dues</Text>
			<Text style={styles.help}>
				{parValue
					? 'Each account is billed its ownership percentage of the annual budget below. Cadence and day of month control when those dues post.'
					: 'Base monthly/quarterly/annual amount charged to every account in this association. Per-account overrides (fixed amount, percent, or par-value share) live on the account itself.'}
			</Text>
			{/* Par-value mode computes every amount, so there is no flat Amount to
			    type — only WHEN dues post remains configurable here. */}
			<View style={{ flexDirection: 'row', gap: 8 }}>
				{!parValue && (
					<View style={{ flex: 1 }}>
						<Text style={styles.label}>Amount</Text>
						<TextInput placeholderTextColor="#bdbdbd" style={styles.input} value={amount} onChangeText={setAmount} placeholder="125.00" keyboardType="decimal-pad" editable={canModify} />
					</View>
				)}
				<View style={{ flex: 1 }}>
					<Text style={styles.label}>Day of month</Text>
					<TextInput placeholderTextColor="#bdbdbd" style={styles.input} value={dayOfMonth} onChangeText={setDayOfMonth} placeholder="1" keyboardType="number-pad" editable={canModify} />
				</View>
			</View>
			<Text style={styles.label}>Cadence</Text>
			<View style={styles.chipRow}>
				{CADENCES.map((c) => (
					<Pressable key={c} disabled={!canModify} onPress={() => setCadence(cadence === c ? undefined : c)} style={[styles.chip, cadence === c && styles.chipActive, !canModify && { opacity: 0.4 }]}>
						<Text style={[styles.chipLabel, cadence === c && styles.chipLabelActive]}>{c}</Text>
					</Pressable>
				))}
			</View>
			<Text style={styles.label}>Billing mode</Text>
			<Pressable
				accessibilityRole="switch"
				accessibilityState={{ checked: parValue }}
				disabled={!canModify}
				onPress={() => setParValue((v) => !v)}
				style={[styles.toggleRow, !canModify && { opacity: 0.5 }]}
			>
				<View style={[styles.toggleTrack, parValue && styles.toggleTrackOn]}>
					<View style={[styles.toggleThumb, parValue && styles.toggleThumbOn]} />
				</View>
				<View style={{ flex: 1 }}>
					<Text style={styles.toggleLabel}>Par Value (share of budget)</Text>
					<Text style={styles.toggleHint}>
						When on, each account&apos;s dues = the annual operating budget × the
						account&apos;s ownership percentage ÷ periods per year. Accounts without a
						percentage fall back to their level or the default amount.
					</Text>
				</View>
			</Pressable>
			{parValue && (
				<View>
					{/* No hand-typed budget: the annual operating budget IS the total of
					    the Annual Budget by GL table below, so there is only one number
					    and nothing to drift from the adopted budget. */}
					<Text style={styles.toggleHint}>
						Set the annual budget in the <Text style={{ fontWeight: '700' }}>Annual Budget by GL</Text> table
						below, and each account&apos;s ownership percentage on the customer&apos;s Dues panel.
					</Text>
					<Text style={styles.label}>Rounding</Text>
					<View style={styles.chipRow} role="radiogroup">
						{ROUNDING_OPTIONS.map((o) => (
							<Pressable
								key={o.value}
								role="radio"
								aria-checked={rounding === o.value}
								disabled={!canModify}
								onPress={() => setRounding(o.value)}
								style={[styles.chip, rounding === o.value && styles.chipActive, !canModify && { opacity: 0.4 }]}
							>
								<Text style={[styles.chipLabel, rounding === o.value && styles.chipLabelActive]}>{o.label}</Text>
							</Pressable>
						))}
					</View>
					<Text style={styles.toggleHint}>{ROUNDING_OPTIONS.find((o) => o.value === rounding)?.hint}</Text>
				</View>
			)}
			{canModify && !hideSaveButton && (
				<Pressable style={[styles.primary, update.isPending && { opacity: 0.5 }]} disabled={update.isPending} onPress={save}>
					<Text style={styles.primaryLabel}>{update.isPending ? 'Saving…' : 'Save default dues'}</Text>
				</Pressable>
			)}
		</View>
	);
});

AssociationSection.displayName = 'AssociationSection';

// --- Multi-GL split lines (T-chart) ---------------------------------------

type GlOption = { id: string; code: string; label: string };

// Codes most associations split assessments into. Surfaced as one-tap
// quick-add buttons; otherwise PMs hunt them in a 1000-row picker.
const HIGHLIGHTED_GL_CODES = ['3000', '3010'];

const LinesSection: React.FC<{
	associationId: string;
	lines: AssociationDuesLine[];
	duesAmountCents?: number;
	/** In par-value mode each line is a GL code's ANNUAL budget and their total is
	 *  the annual operating budget, so the "must sum to the default dues" check
	 *  doesn't apply — there is no default dues amount. */
	parValue?: boolean;
	cadence?: AssessmentCadence;
	canModify: boolean;
	/** When set, lines are scoped to a single dues level — the editor tags
	 *  new/edited rows with this level_id and the totals check uses the
	 *  level's amount instead of the association default. */
	levelId?: string;
	/** Optional label override; defaults to "GL split lines". */
	title?: string;
}> = ({ associationId, lines, duesAmountCents, parValue, cadence, canModify, levelId, title }) => {
	const { data: glResp } = useGlCodes({ limit: 1000, activeOnly: true });
	const glOptions = useMemo<GlOption[]>(() => {
		const rows = (glResp?.data ?? []) as GlCode[];
		const income = rows.filter((g) => g.accountType === 'INC' || g.accountType === 'OINC');
		const rest = rows.filter((g) => g.accountType !== 'INC' && g.accountType !== 'OINC');
		return [...income, ...rest].map((g) => ({ id: g.id, code: g.code, label: `${g.code} — ${g.fullPath || g.name}` }));
	}, [glResp]);
	const usedGlIds = useMemo(() => new Set(lines.map((l) => l.glCodeId).filter(Boolean) as string[]), [lines]);
	const highlights = useMemo(
		() => HIGHLIGHTED_GL_CODES.map((code) => glOptions.find((o) => o.code === code)).filter((x): x is GlOption => !!x && !usedGlIds.has(x.id)),
		[glOptions, usedGlIds],
	);

	const create = useCreateDuesLine();
	const remove = useDeleteDuesLine();
	const update = useUpdateDuesLine();
	const [importOpen, setImportOpen] = useState(false);

	const linesTotal = lines.reduce((s, l) => s + (l.amountCents || 0), 0);
	// Par-value mode has no flat dues amount for the lines to reconcile against —
	// the lines ARE the budget, so any total is valid by definition.
	const totalsMatch = parValue || duesAmountCents == null || linesTotal === duesAmountCents;
	const remaining = !parValue && duesAmountCents != null ? duesAmountCents - linesTotal : 0;

	const addLine = (glCodeId: string | null, amountCents: number) => {
		create.mutate({
			associationId,
			body: { glCodeId, levelId: levelId ?? null, label: undefined, amountCents, sortOrder: lines.length },
		});
	};

	return (
		<View style={levelId ? styles.nestedSection : styles.section}>
			<Text style={styles.sectionTitle}>{title ?? (parValue ? 'Annual budget by GL' : 'GL split lines')}</Text>
			<Text style={styles.help}>
				{levelId
					? 'Optional per-level split. When set, the cron uses these GL codes for accounts on this level instead of the default split.'
					: parValue
						? 'Each line is the ANNUAL budget for that GL code, and their total is the association\'s annual operating budget. Every account is billed its ownership percentage of that total, split across these codes.'
						: 'Optional. When configured, the dues posting fans out into one charge per line — each tagged with its own GL code. Amounts must sum to the default dues. Leave empty to post a single un-split charge.'}
			</Text>

			<View style={styles.table}>
				<View style={styles.tableHeader}>
					<Text style={[styles.tableHeaderCell, { flex: 1 }]}>GL code</Text>
					<Text style={[styles.tableHeaderCell, styles.amountCell]}>Amount</Text>
					{canModify && <View style={styles.actionCol} />}
				</View>
				{lines.length === 0 && (
					<View style={styles.tableRow}>
						<Text style={[styles.empty, { flex: 1 }]}>
							{parValue
								? 'No annual budget set — add a GL line before posting dues.'
								: 'No splits — posts a single charge per period.'}
						</Text>
					</View>
				)}
				{lines.map((l) => (
					<LineRow
						key={l.id}
						line={l}
						glOptions={glOptions}
						canModify={canModify}
						onDelete={() => remove.mutate({ associationId, lineId: l.id })}
						onEdit={(body) => update.mutate({ associationId, lineId: l.id, body })}
					/>
				))}
				{lines.length > 0 && (
					<View style={[styles.tableRow, styles.totalRow]}>
						<Text style={[styles.totalLabel, { flex: 1 }]}>{parValue ? 'Annual operating budget' : 'Total'}</Text>
						<Text style={[styles.totalValue, styles.amountCell, !totalsMatch && styles.warn]}>{formatMoneyCents(linesTotal)}</Text>
						{canModify && <View style={styles.actionCol} />}
					</View>
				)}
				{/* Self-checking readout: the whole association's dues for one period.
				    A budget keyed in monthly by mistake shows up here immediately. */}
				{parValue && lines.length > 0 && (
					<View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
						<Text style={[styles.help, { flex: 1, fontSize: 12 }]}>
							{formatMoneyCents(Math.round(linesTotal / periodsPerYear(cadence)))} per {cadence ?? 'monthly'} period
							across all owners, divided by each account&apos;s ownership percentage.
						</Text>
					</View>
				)}
				{lines.length > 0 && duesAmountCents != null && !totalsMatch && (
					<View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
						<Text style={[styles.warn, { flex: 1, fontSize: 12 }]}>
							{remaining > 0 ? `Short ${formatMoneyCents(remaining)}` : `Over by ${formatMoneyCents(-remaining)}`}
							{` (default dues ${formatMoneyCents(duesAmountCents)})`}
						</Text>
					</View>
				)}
			</View>

			{canModify && parValue && !levelId && (
				<>
					<Pressable style={styles.importRow} onPress={() => setImportOpen(true)}>
						<Text style={styles.importLabel}>⭱ Import annual budget from CSV</Text>
					</Pressable>
					{importOpen && (
						<ImportBudgetModal associationId={associationId} cadence={cadence} onClose={() => setImportOpen(false)} />
					)}
				</>
			)}

			{canModify && (
				<View style={styles.quickAddRow}>
					{highlights.map((opt) => (
						<Pressable
							key={opt.id}
							style={styles.quickAddChip}
							disabled={create.isPending}
							onPress={() => addLine(opt.id, remaining > 0 ? remaining : 0)}
						>
							<Text style={styles.quickAddLabel}>+ {opt.label}</Text>
						</Pressable>
					))}
					<NewLineButton
						glOptions={glOptions}
						defaultAmountCents={remaining > 0 ? remaining : 0}
						disabled={create.isPending}
						onAdd={addLine}
					/>
				</View>
			)}
		</View>
	);
};

const NewLineButton: React.FC<{
	glOptions: GlOption[];
	defaultAmountCents: number;
	disabled: boolean;
	onAdd: (glCodeId: string | null, amountCents: number) => void;
}> = ({ glOptions, defaultAmountCents, disabled, onAdd }) => {
	const [open, setOpen] = useState(false);
	const [glId, setGlId] = useState<string | undefined>();
	const [amount, setAmount] = useState((defaultAmountCents / 100).toFixed(2));

	if (!open) {
		return (
			<Pressable style={styles.quickAddChip} onPress={() => { setAmount((defaultAmountCents / 100).toFixed(2)); setOpen(true); }}>
				<Text style={styles.quickAddLabel}>+ Add another GL</Text>
			</Pressable>
		);
	}
	return (
		<View style={{ width: '100%', marginTop: 6 }}>
			<View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-end' }}>
				<View style={{ flex: 2 }}>
					<Text style={styles.label}>GL code</Text>
					<LocalTypeahead<GlOption>
						items={glOptions}
						toOption={(o) => ({ id: o.id, label: o.label })}
						value={glId}
						onChange={(id) => setGlId(id ?? undefined)}
						placeholder="Search GL codes…"
					/>
				</View>
				<View style={{ width: 110 }}>
					<Text style={styles.label}>Amount</Text>
					<TextInput placeholderTextColor="#bdbdbd" style={styles.input} value={amount} onChangeText={setAmount} keyboardType="decimal-pad" />
				</View>
			</View>
			<View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
				<Pressable
					style={[styles.secondary, { flex: 1 }, disabled && { opacity: 0.5 }]}
					disabled={disabled}
					onPress={() => {
						const cents = parseDollarsToCents(amount);
						if (cents == null || cents < 0) { Alert.alert('Invalid amount'); return; }
						onAdd(glId ?? null, cents);
						setOpen(false); setGlId(undefined); setAmount('');
					}}
				>
					<Text style={styles.secondaryLabel}>Add line</Text>
				</Pressable>
				<Pressable style={[styles.secondary, { flex: 1 }]} onPress={() => setOpen(false)}>
					<Text style={styles.secondaryLabel}>Cancel</Text>
				</Pressable>
			</View>
		</View>
	);
};

const LineRow: React.FC<{
	line: AssociationDuesLine;
	glOptions: GlOption[];
	canModify: boolean;
	onDelete: () => void;
	onEdit: (body: { glCodeId?: string | null; label?: string | null; amountCents?: number }) => void;
}> = ({ line, glOptions, canModify, onDelete, onEdit }) => {
	const [amount, setAmount] = useState((line.amountCents / 100).toFixed(2));
	const dirty = Math.round(Number(amount) * 100) !== line.amountCents;
	const glLabel = useMemo(
		() => glOptions.find((o) => o.id === line.glCodeId)?.label ?? '— no GL —',
		[glOptions, line.glCodeId],
	);
	const save = () => {
		const cents = parseDollarsToCents(amount);
		if (cents == null || cents < 0) { Alert.alert('Invalid amount'); return; }
		onEdit({ amountCents: cents });
	};
	return (
		<View style={styles.tableRow}>
			<Text style={[styles.glCell, { flex: 1 }]} numberOfLines={1}>{glLabel}</Text>
			<TextInput
				style={[styles.input, styles.amountCell, styles.amountInput]}
				value={amount}
				onChangeText={setAmount}
				onBlur={() => { if (dirty) save(); }}
				keyboardType="decimal-pad"
				editable={canModify}
			/>
			{canModify && (
				<View style={styles.actionCol}>
					<Pressable hitSlop={6} onPress={onDelete}><Text style={styles.actionDanger}>✕</Text></Pressable>
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	section: { marginTop: 12, padding: 14, backgroundColor: '#f5f7fa', borderRadius: 8 },
	importRow: { paddingVertical: 8, alignItems: 'flex-start' },
	importLabel: { color: '#1976d2', fontWeight: '600', fontSize: 13 },
	draftHint: { marginTop: 10, fontSize: 12, color: '#78909c', fontStyle: 'italic' },
	nestedSection: { marginTop: 8, marginLeft: 16, marginBottom: 4, padding: 10, backgroundColor: '#fff', borderRadius: 6, borderWidth: 1, borderColor: '#cfd8dc' },
	toggleRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 8 },
	toggleTrack: { width: 36, height: 20, borderRadius: 10, backgroundColor: '#cfd8dc', padding: 2, justifyContent: 'center' },
	toggleTrackOn: { backgroundColor: '#1976d2' },
	toggleThumb: { width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff' },
	toggleThumbOn: { alignSelf: 'flex-end' },
	toggleLabel: { fontSize: 14, fontWeight: '600', color: '#212121' },
	toggleHint: { fontSize: 12, color: '#546e7a', marginTop: 2 },
	sectionTitle: { fontSize: 14, fontWeight: '700', color: '#212121', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 },
	help: { fontSize: 12, color: '#546e7a', marginBottom: 10 },
	label: { marginTop: 8, marginBottom: 4, fontSize: 11, color: '#546e7a', fontWeight: '600', textTransform: 'uppercase' },
	input: { borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6, padding: 10, fontSize: 14, backgroundColor: '#fff' },
	inputReadOnly: { backgroundColor: '#eceff1', color: '#666' },
	chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8 },
	chip: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: 14, backgroundColor: '#e0e0e0' },
	chipActive: { backgroundColor: '#1976d2' },
	chipLabel: { fontSize: 12, color: '#333' },
	chipLabelActive: { color: '#fff', fontWeight: '600' },
	levelRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
	levelNumber: { width: 28, fontWeight: '700', color: '#37474f', textAlign: 'center' },
	actionLink: { color: '#1976d2', fontWeight: '600', paddingHorizontal: 6 },
	actionDanger: { color: '#c62828', fontSize: 18, paddingHorizontal: 6 },
	primary: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center', marginTop: 12 },
	primaryLabel: { color: '#fff', fontWeight: '600' },
	secondary: { backgroundColor: '#fff', padding: 10, borderRadius: 6, alignItems: 'center', marginTop: 8, borderWidth: 1, borderColor: '#cfd8dc' },
	secondaryLabel: { color: '#1976d2', fontWeight: '500' },
	empty: { color: '#888', fontStyle: 'italic' },
	muted: { color: '#78909c', fontSize: 11 },
	warn: { color: '#c62828' },
	totalsRow: { paddingVertical: 6, alignItems: 'flex-end' },
	lineRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#eceff1' },
	table: { borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6, backgroundColor: '#fff', overflow: 'hidden' },
	tableHeader: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#eceff1', paddingHorizontal: 10, paddingVertical: 8, gap: 8 },
	tableHeaderCell: { fontSize: 11, fontWeight: '700', color: '#546e7a', textTransform: 'uppercase', letterSpacing: 0.4 },
	tableRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, gap: 8, borderTopWidth: 1, borderTopColor: '#eceff1' },
	totalRow: { backgroundColor: '#fafafa' },
	totalLabel: { fontWeight: '700', color: '#212121' },
	totalValue: { fontWeight: '700', color: '#212121' },
	glCell: { color: '#212121', fontSize: 14 },
	amountCell: { width: 110, textAlign: 'right' },
	amountInput: { paddingVertical: 6, paddingHorizontal: 8 },
	actionCol: { width: 28, alignItems: 'center' },
	quickAddRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
	quickAddChip: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 6, backgroundColor: '#e3f2fd', borderWidth: 1, borderColor: '#90caf9' },
	quickAddLabel: { color: '#1565c0', fontWeight: '600', fontSize: 13 },
});
